import { useEffect, useState } from 'react';
import {
  Button,
  Form,
  message,
  Modal,
  notification,
  Space,
  Switch,
} from 'antd';
import { useIntl } from 'react-intl';

import { queryClient } from '@/Root';

import AccessoriesGeneralForm from '../Form';
import {
  useAccessory,
  useActiveOrInactivateAccessory,
  useCreateVehicleAccessory,
  useUpdateVehicleAccessory,
} from '@/core/domain/people/Accessory/Accessory.hooks';
import { VehicleAccessoryFormValues } from '@/core/domain/people/Accessory/Accessory.types';

interface AccessoriesGeneralModalFormProps {
  accessory?: number;
  visible: boolean;
  onCancel: () => void;
}

function AccessoriesGeneralModalForm({
  accessory,
  visible,
  onCancel,
}: AccessoriesGeneralModalFormProps) {
  const intl = useIntl();
  const [form] = Form.useForm<VehicleAccessoryFormValues>();
  const [active, setActive] = useState<boolean>();

  const { data, mutateAsync, isLoading } = useAccessory();
  const createMutation = useCreateVehicleAccessory();
  const updateMutation = useUpdateVehicleAccessory();
  const activateOrInactivateMutation = useActiveOrInactivateAccessory();

  useEffect(() => {
    if (accessory) {
      mutateAsync(accessory);
    }
  }, [accessory]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        description: data.description,
        checkList: data.checkList,
      });
    }
    setActive(data?.active);
  }, [data]);

  async function handleCreateAccessory() {
    const values = await form.validateFields();

    await createMutation.mutateAsync({ ...values, active: true });
    await queryClient.refetchQueries('accessories');

    handleOnCancel();
  }

  async function handleUpdateAccessory() {
    const values = await form.validateFields();

    if (!data) return;

    const obj = {
      active: active,
      id: data?.id,
      ...values,
    };

    await updateMutation.mutateAsync(obj);
    await queryClient.refetchQueries('accessories');

    handleOnCancel();
  }

  const onFinish = () => {
    try {
      if (accessory) {
        // Service update
        handleUpdateAccessory();
      } else {
        // Service create
        handleCreateAccessory();
      }
    } catch (error) {
      message.error(
        intl.formatMessage({ id: 'accessories.modal.message.error' }),
      );
    }
  };

  const handleOnCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      if (data) {
        activateOrInactivateMutation.mutateAsync(Number(data.id));
      }

      setActive(active);
    } catch (error) {
      notification.error({
        message: intl.formatMessage({
          id: 'commons.form.notification.error',
        }),
        description: intl.formatMessage({
          id: 'commons.form.notification.errorDescription',
        }),
      });
    }
  };

  return (
    <Modal
      open={visible}
      okText="Criar"
      onCancel={handleOnCancel}
      confirmLoading={
        isLoading || createMutation.isLoading || updateMutation.isLoading
      }
      title={
        accessory
          ? intl.formatMessage({ id: 'accessories.modal.edit.title' })
          : intl.formatMessage({ id: 'accessories.modal.create.title' })
      }
      footer={
        <Space>
          {accessory && (
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={false}
              checked={active}
              onClick={handleActiveClick}
            />
          )}

          <Button onClick={handleOnCancel}>
            {intl.formatMessage({ id: 'commons.modal.button.cancel' })}
          </Button>

          <Button
            onClick={onFinish}
            className="save-button"
            style={{ display: 'inline' }}
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            {intl.formatMessage({ id: 'commons.form.button.save' })}
          </Button>
        </Space>
      }
    >
      <AccessoriesGeneralForm form={form} />
    </Modal>
  );
}

export default AccessoriesGeneralModalForm;
